import { DivisionsTreeNode } from "@rmp/core/utils/helpers";

export interface DivisionScopeOption {
	id: string;
	depth: number;
	text: string;
	code: string;
	type: DivisionsTreeNode["type"];
	isArchived: boolean;
	canActAsServiceProvider: boolean;
}

export interface DivisionScopeDisplayItem {
	id: string;
	text: string;
	code: string;
	depth: number;
	isArchived: boolean;
	unknown: boolean;
}

export interface DivisionScopeStructureItem {
	id: string;
	division: {
		parentId?: string | null;
		isArchived?: boolean;
	};
}

export interface DivisionScopeStructure {
	head?: { id: string } | null;
	branches: readonly DivisionScopeStructureItem[];
	departments: readonly DivisionScopeStructureItem[];
	ports: readonly DivisionScopeStructureItem[];
}

export const normalizeDivisionIds = (ids: readonly (string | null | undefined)[]): string[] =>
	[...new Set(ids.filter((id): id is string => Boolean(id && id !== "00000000-0000-0000-0000-000000000000")))]
		.sort((left, right) => left.localeCompare(right));

export const getAppliedDivisionIds = (source?: {
	appliedDivisionIds?: string[] | null;
} | null): string[] => normalizeDivisionIds(source?.appliedDivisionIds ?? []);

export const flattenDivisionTree = (nodes: DivisionsTreeNode[], depth = 0): DivisionScopeOption[] =>
	nodes.flatMap(node => [
		{
			id: node.id,
			depth,
			text: node.data.division.name,
			code: node.data.division.code,
			type: node.type,
			isArchived: node.data.division.isArchived,
			canActAsServiceProvider: node.data.division.canActAsServiceProvider
		},
		...flattenDivisionTree(node.children, depth + 1)
	]);

export const isSelectableServiceProviderDivision = (item: DivisionScopeOption): boolean =>
	item.type !== "port" && !item.isArchived && item.canActAsServiceProvider;

/**
 * Канонический ответ gateway о доступных сотруднику подразделениях.
 * Описан структурно, чтобы утилита не зависела от конкретного API-пакета.
 */
export interface DivisionAccessFilterLike {
	kind: "Unrestricted" | "Empty" | "Exact" | string;
	divisionIds: string[];
}

/** Оставляет в дереве только разрешённые узлы, поднимая доступных потомков над скрытым родителем. */
export const filterDivisionTreeByIds = (
	nodes: DivisionsTreeNode[],
	allowedIds: Set<string>
): DivisionsTreeNode[] =>
	nodes.flatMap(node => {
		const children = filterDivisionTreeByIds(node.children, allowedIds);
		return allowedIds.has(node.id)
			? [{ ...node, children }]
			: children;
	});

/** Применяет канонический access filter к готовому дереву, не раскрывая exact scope. */
export const withDivisionAccess = (
	nodes: DivisionsTreeNode[],
	access: DivisionAccessFilterLike
): DivisionsTreeNode[] => {
	if(access.kind === "Unrestricted") return nodes;
	if(access.kind === "Exact") return filterDivisionTreeByIds(nodes, new Set(access.divisionIds));
	
	return [];
};

/** Сужает дерево до физического поддерева rootId, не расширяя ролевой scope. */
export const withinDivisionSubtree = (
	nodes: DivisionsTreeNode[],
	rootDivisionId: string | undefined,
	structure: DivisionScopeStructure
): DivisionsTreeNode[] => {
	if(!rootDivisionId) return nodes;
	
	return filterDivisionTreeByIds(nodes, new Set(getRootedDivisionScopeIds(rootDivisionId, structure)));
};

/** Оставляет только подразделения, которые могут выступать оказывающими услуги. */
export const onlyServiceProviderDivisions = (nodes: DivisionsTreeNode[]): DivisionsTreeNode[] =>
	filterDivisionTreeByIds(nodes, new Set(flattenDivisionTree(nodes)
	.filter(isSelectableServiceProviderDivision)
	.map(item => item.id)));

/** Оставляет только активные порты, доступные в исходном дереве. */
export const onlyActivePorts = (nodes: DivisionsTreeNode[]): DivisionsTreeNode[] =>
	filterDivisionTreeByIds(nodes, new Set(flattenDivisionTree(nodes)
	.filter(item => item.type === "port" && !item.isArchived)
	.map(item => item.id)));

const compareDivisionScopeItems = (left: DivisionScopeDisplayItem, right: DivisionScopeDisplayItem): number =>
	left.text.localeCompare(right.text, "ru", { sensitivity: "base" })
		|| left.code.localeCompare(right.code, "ru", { sensitivity: "base" })
		|| left.id.localeCompare(right.id);

const toDivisionScopeDisplayItem = (option: DivisionScopeOption): DivisionScopeDisplayItem => ({
	id: option.id,
	text: option.text,
	code: option.code,
	depth: option.depth,
	isArchived: option.isArchived,
	unknown: false
});

export const getDivisionScopeSelectionItems = (
	nodes: DivisionsTreeNode[],
	selectedIds: readonly string[],
	getUnknownText: (id: string) => string
): DivisionScopeDisplayItem[] => {
	const knownItems = flattenDivisionTree(nodes).map(toDivisionScopeDisplayItem);
	const knownIds = new Set(knownItems.map(item => item.id));
	const unknownItems = normalizeDivisionIds(selectedIds)
		.filter(id => !knownIds.has(id))
		.map(id => ({
			id,
			text: getUnknownText(id),
			code: "",
			depth: 0,
			isArchived: false,
			unknown: true
		}));

	return [...knownItems, ...unknownItems.sort(compareDivisionScopeItems)];
};

export const getDivisionScopeDisplayItems = (
	ids: readonly (string | null | undefined)[],
	nodes: DivisionsTreeNode[],
	getUnknownText: (id: string) => string
): DivisionScopeDisplayItem[] => {
	const itemById = new Map(flattenDivisionTree(nodes)
		.map(toDivisionScopeDisplayItem)
		.map(item => [item.id, item] as const));

	return normalizeDivisionIds(ids)
		.map(id => itemById.get(id) ?? {
			id,
			text: getUnknownText(id),
			code: "",
			depth: 0,
			isArchived: false,
			unknown: true
		})
		.sort(compareDivisionScopeItems);
};

export const getDivisionScopeDisplayText = (
	ids: readonly (string | null | undefined)[],
	nodes: DivisionsTreeNode[],
	getUnknownText: (id: string) => string
): string => getDivisionScopeDisplayItems(ids, nodes, getUnknownText)
	.map(item => item.text)
	.join(", ");

const getDivisionScopeStructureItems = (structure: DivisionScopeStructure): DivisionScopeStructureItem[] => [
	...(structure.head?.id ? [{ id: structure.head.id, division: {} }] : []),
	...structure.branches,
	...structure.departments,
	...structure.ports
];

export const getRootedDivisionScopeIds = (
	rootDivisionId: string,
	structure: DivisionScopeStructure
): string[] => {
	const items = getDivisionScopeStructureItems(structure)
		.filter(item => !item.division.isArchived);
	const knownIds = new Set(items.map(item => item.id));
	if(!knownIds.has(rootDivisionId)) return [];

	// Head хранится отдельным корнем UI-дерева, поэтому поддерево собираем только по фактическим parentId.
	const childrenByParent = new Map<string, string[]>();
	items.forEach(item => {
		const parentId = item.division?.parentId;
		if(!parentId) return;

		const children = childrenByParent.get(parentId) ?? [];
		children.push(item.id);
		childrenByParent.set(parentId, children);
	});

	const result = new Set<string>();
	const pending = [rootDivisionId];
	while(pending.length) {
		const currentId = pending.pop()!;
		if(result.has(currentId) || !knownIds.has(currentId)) continue;

		result.add(currentId);
		pending.push(...(childrenByParent.get(currentId) ?? []));
	}

	return normalizeDivisionIds([...result]);
};

export const normalizeRootedDivisionScope = (
	ids: readonly (string | null | undefined)[],
	rootDivisionId: string,
	structure: DivisionScopeStructure
): string[] => {
	const allowedIds = getRootedDivisionScopeIds(rootDivisionId, structure);
	if(!allowedIds.length) return [];

	const allowedSet = new Set(allowedIds);
	return normalizeDivisionIds([
		...ids.filter((id): id is string => Boolean(id && allowedSet.has(id))),
		rootDivisionId
	]);
};

/**
 * AppliedDivisionIds задают подразделения, отвечающие за рассмотрение заявок.
 * Роль перехода может принадлежать им или их физическим предкам, кроме портов.
 */
export const getWorkflowRoleDivisionIds = (
	appliedDivisionIds: readonly (string | null | undefined)[],
	structure: DivisionScopeStructure
): string[] => {
	const items = getDivisionScopeStructureItems(structure);
	const parentIdByDivisionId = new Map(items.map(item => [item.id, item.division.parentId ?? undefined]));
	const roleDivisionIds = new Set([
		...(structure.head?.id ? [structure.head.id] : []),
		...structure.branches.map(branch => branch.id),
		...structure.departments.map(department => department.id)
	]);
	const visited = new Set<string>();
	const result = new Set<string>();

	const visit = (divisionId: string) => {
		if(visited.has(divisionId) || !parentIdByDivisionId.has(divisionId)) return;
		visited.add(divisionId);

		if(roleDivisionIds.has(divisionId)) result.add(divisionId);

		const parentId = parentIdByDivisionId.get(divisionId);
		if(parentId) visit(parentId);
	};

	normalizeDivisionIds(appliedDivisionIds).forEach(visit);

	return normalizeDivisionIds([...result]);
};

interface ProviderPortScopeStructure {
	head?: { id: string };
	branches: { id: string; division: { parentId?: string | null } }[];
	departments: { id: string; division: { parentId?: string | null } }[];
	ports: { id: string; division: { parentId?: string | null } }[];
}

/**
 * Возвращает предметные порты оказывающего подразделения только по разрешённым фиксированным связям.
 * Эта функция не вычисляет область видимости объекта.
 */
export const getProviderPortIds = (providerId: string, structure: ProviderPortScopeStructure): string[] => {
	const branchIds = new Set<string>();
	const departmentIds = new Set<string>();

	if(structure.head?.id === providerId) {
		structure.branches
			.filter(branch => branch.division.parentId === providerId)
			.forEach(branch => branchIds.add(branch.id));
	} else if(structure.branches.some(branch => branch.id === providerId)) {
		branchIds.add(providerId);
	} else if(structure.departments.some(department => department.id === providerId)) {
		departmentIds.add(providerId);
	} else {
		return [];
	}

	structure.departments
		.filter(department => department.division.parentId && branchIds.has(department.division.parentId))
		.forEach(department => departmentIds.add(department.id));

	return normalizeDivisionIds(structure.ports
		.filter(port => Boolean(port.division.parentId
			&& (branchIds.has(port.division.parentId) || departmentIds.has(port.division.parentId))))
		.map(port => port.id));
};
